import cn from 'classnames/bind'
import { ChangeEvent, FocusEvent, useState } from 'react'

import PlainInput, { PlainInputProps } from './plain-input'

import styles from './plain-input-currency.module.scss'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'

const cx = cn.bind(styles)

const normalizeCurrencyInput = (value: string, integer: boolean) => {
  let normalized = value
    .replace(/\s+/g, '')
    .replace(/[^\d.,]/g, '')
    .replace(/\./g, ',')

  const firstCommaIndex = normalized.indexOf(',')

  if (firstCommaIndex !== -1) {
    normalized =
      normalized.slice(0, firstCommaIndex + 1) +
      normalized.slice(firstCommaIndex + 1).replace(/,/g, '')
  }

  if (integer) {
    normalized = normalized.replace(/,/g, '')
  }

  return normalized
}

type Props = {
  hideArrows?: boolean
  currency?: string
  integer?: boolean
  onChangeValue?: (value: string, valueFormatted: string) => void
} & PlainInputProps

export const PlainInputCurrency = ({
  hideArrows,
  value,
  defaultValue,
  currency = '₽',
  integer = false,
  inputClassName,
  onChange,
  onChangeValue,
  ...others
}: Props) => {
  const isControlled = value !== undefined

  const [enteredValue, setEnteredValue] = useState<string>()
  const [focused, setFocused] = useState(false)

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (enteredValue === '' || enteredValue == null) {
      setFocused(false)
      return
    }

    const asNum = Number(String(enteredValue).replace(',', '.'))
    if (!isNaN(asNum)) {
      const valueFixed = asNum.toFixed(integer ? 0 : 2)
      setEnteredValue(valueFixed)
      const valueFormatted = toFixedValueCurrency(valueFixed, integer, currency)
      onChangeValue?.(valueFixed, valueFormatted)
    }
    setFocused(false)
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(true)

    const normalized = normalizeCurrencyInput(String(currentValue ?? ''), integer)

    if (!isControlled) {
      setEnteredValue(normalized)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e)
    const normalized = normalizeCurrencyInput(e.currentTarget.value, integer)

    e.currentTarget.value = normalized

    if (normalized === '') {
      setEnteredValue('')
      onChangeValue?.('', '')
      return
    }

    setEnteredValue(normalized)

    const asNum = Number(normalized.replace(',', '.'))
    if (isNaN(asNum)) return
    const valueFixed = asNum.toFixed(integer ? 0 : 2)
    const valueFormatted = toFixedValueCurrency(valueFixed, integer, currency)
    onChangeValue?.(valueFixed, valueFormatted)
  }

  const currentValue = isControlled ? value : (enteredValue ?? defaultValue)
  const formattedValue = toFixedValueCurrency(currentValue, integer, currency)

  return (
    <PlainInput
      type="text"
      value={focused ? currentValue : formattedValue}
      onChange={handleChange}
      inputClassName={cx(inputClassName, { hideArrows })}
      inputmode={integer ? 'numeric' : 'decimal'}
      onBlur={handleBlur as (e: ChangeEvent<HTMLInputElement>) => void}
      onFocus={handleFocus as (e: ChangeEvent<HTMLInputElement>) => void}
      {...others}
    />
  )
}
